import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Save, CalendarClock, ChevronDown, Link2, CalendarCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHeader, Spinner } from "@/components/ui-bits";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";
import { ConnectCalendar } from "@/components/ConnectCalendar";
import { CalendarOAuthConnect } from "@/components/CalendarOAuthConnect";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { fmtRelative } from "@/lib/constants";
import { toast } from "sonner";

export default function ExpertAvailability() {
  const { t, i18n } = useTranslation();
  const [slots, setSlots] = useState(null);
  const [busy, setBusy] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBadges, setSyncBadges] = useState([]);
  const [, setTick] = useState(0);

  const loadSyncStatus = useCallback(() => {
    Promise.all([
      api.get("/expert/calendar/google").then((r) => r.data).catch(() => null),
      api.get("/expert/calendar/microsoft").then((r) => r.data).catch(() => null),
    ]).then(([g, m]) => setSyncBadges([
      { provider: "google", ns: "gcal", label: "Google", connected: !!g?.connected, last_sync: g?.last_sync },
      { provider: "microsoft", ns: "mscal", label: "Microsoft", connected: !!m?.connected, last_sync: m?.last_sync },
    ]));
  }, []);

  const loadExpert = async () => {
    try {
      const { data } = await api.get("/expert/me");
      setBusy(data?.external_busy || []);
      setBlocks(data?.time_blocks || []);
      return data?.availability || [];
    } catch { return []; }
  };

  useEffect(() => {
    loadExpert().then((a) => setSlots(a));
    api.get("/bookings/mine").then(({ data }) => setBookings(data || [])).catch(() => setBookings([]));
    loadSyncStatus();
  }, [loadSyncStatus]);

  // Refresh the relative "last synced" labels every minute
  useEffect(() => { const id = setInterval(() => setTick((x) => x + 1), 60000); return () => clearInterval(id); }, []);

  const refreshBusy = async () => {
    const avail = await loadExpert();
    const busyByStart = {};
    avail.forEach((s) => { busyByStart[s.start] = s.busy; });
    setSlots((prev) => (prev || []).map((s) => ({ ...s, busy: !!busyByStart[s.start] })));
    loadSyncStatus();
  };

  const addBlock = async (draft) => {
    try {
      const { data } = await api.post("/expert/time-blocks", draft);
      setBlocks((prev) => [...prev, data]);
      toast.success(t("cal.toastBlockAdded"));
    } catch (err) { toast.error(apiError(err, t("cal.toastBlockError"))); }
  };

  const deleteBlock = async (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    try { await api.delete(`/expert/time-blocks/${id}`); toast.success(t("cal.toastBlockRemoved")); }
    catch (err) { toast.error(apiError(err)); refreshBusy(); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.post("/expert/availability", { availability: slots }); toast.success(t("expert.availability.toastSaved")); setDirty(false); await refreshBusy(); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <AppShell>
      <PageHeader eyebrow={t("expert.eyebrow")} title={t("expert.availability.title")} description={t("expert.availability.subtitle")}>
        <Button data-testid="save-availability" onClick={save} disabled={saving || !dirty} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
          {saving ? <Spinner /> : <><Save size={16} /> {t("common.save")}</>}
        </Button>
      </PageHeader>

      {slots === null ? (
        <div className="grid place-items-center py-24"><Spinner className="text-primary w-7 h-7" /></div>
      ) : (
        <div className="space-y-5 animate-fade-up">
          {/* Connected calendars (collapsible) */}
          <Collapsible open={syncOpen} onOpenChange={setSyncOpen} className="card-milled rounded-[0.8rem] overflow-hidden">
            <CollapsibleTrigger data-testid="sync-toggle" className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-secondary/30 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0"><Link2 size={17} strokeWidth={1.75} /></span>
                <div className="min-w-0">
                  <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground">{t("cal.connectedCalendars")}</h3>
                  <p className="text-sm text-muted-foreground">{t("cal.syncSubtitle")}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {syncBadges.filter((b) => b.connected).map((b) => (
                  <span key={b.provider} data-testid={`sync-badge-${b.provider}`}
                    title={b.last_sync ? t(`${b.ns}.lastSyncRel`, { rel: fmtRelative(b.last_sync, i18n.language) }) : ""}
                    className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-success/30 bg-success/5 text-success px-2.5 py-1">
                    <CalendarCheck size={12} /> {b.label}
                    {b.last_sync ? <span className="text-muted-foreground font-normal">· {fmtRelative(b.last_sync, i18n.language)}</span> : null}
                  </span>
                ))}
                <ChevronDown size={18} className={`text-muted-foreground shrink-0 transition-transform ${syncOpen ? "rotate-180" : ""}`} />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4 space-y-4">
              <CalendarOAuthConnect provider="google" onBusyChange={refreshBusy} />
              <CalendarOAuthConnect provider="microsoft" onBusyChange={refreshBusy} />
              <ConnectCalendar onBusyChange={refreshBusy} />
            </CollapsibleContent>
          </Collapsible>

          <AvailabilityCalendar
            value={slots}
            onChange={(v) => { setSlots(v); setDirty(true); }}
            busy={busy}
            blocks={blocks}
            bookings={bookings}
            onAddBlock={addBlock}
            onDeleteBlock={deleteBlock}
          />

          {dirty && (
            <div className="flex items-center gap-2 text-sm text-warning">
              <CalendarClock size={15} /> {t("expert.availability.unsaved")}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
